import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  TextField,
  Button,
  Typography,
  Paper,
  InputAdornment,
  IconButton,
  Alert,
} from '@mui/material';
import { Visibility, VisibilityOff, Lock, LockReset } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../redux/hook';
import {
  fetchProfile,
  logout,
  selectAuthToken,
  selectMustChangePassword,
} from '../redux/slices/auth.slice';
import { UsersServiceHelpers } from '../utils/api-helpers/UsersService.helpers';
import { extractErrorMessage } from '../utils/errorHelpers';
import BackgroundSVG from '../components/common/BackgroundSVG';
import LlamaLogo from '../components/common/LLamaLogo';
import { colors } from '../styles/theme/colors';

const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/;

const ForceChangePasswordPage: React.FC = () => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const mustChangePassword = useAppSelector(selectMustChangePassword);
  const token = useAppSelector(selectAuthToken);
  const [gateReady, setGateReady] = useState(false);
  const [gateAllowed, setGateAllowed] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const resolveGate = async () => {
      if (success) {
        if (!cancelled) {
          setGateAllowed(true);
          setGateReady(true);
        }
        return;
      }

      // If we already know we must change password, allow immediately.
      if (mustChangePassword) {
        if (!cancelled) {
          setGateAllowed(true);
          setGateReady(true);
        }
        return;
      }

      // No token -> definitely not allowed.
      if (token == null || token === '') {
        if (!cancelled) {
          setGateAllowed(false);
          setGateReady(true);
        }
        return;
      }

      // Token exists. Probe profile; if backend says "must change password", stay on this page.
      try {
        await dispatch(fetchProfile()).unwrap();
        if (!cancelled) {
          setGateAllowed(false);
          setGateReady(true);
        }
      } catch {
        if (!cancelled) {
          setGateAllowed(true);
          setGateReady(true);
        }
      }
    };

    resolveGate();
    return () => {
      cancelled = true;
    };
  }, [dispatch, mustChangePassword, success, token]);

  useEffect(() => {
    if (!gateReady) return;
    if (gateAllowed) return;
    navigate(token == null || token === '' ? '/login' : '/dashboard', { replace: true });
  }, [gateAllowed, gateReady, navigate, token]);

  if (!gateReady || !gateAllowed) return null;

  const validate = (): string | null => {
    if (newPassword.length < 8) return 'La contraseña debe tener al menos 8 caracteres';
    if (newPassword.length > 50) return 'La contraseña no puede exceder 50 caracteres';
    if (!PASSWORD_REGEX.test(newPassword))
      return 'Debe incluir al menos una mayúscula, una minúscula y un número';
    if (newPassword !== confirmPassword) return 'Las contraseñas no coinciden';
    return null;
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsLoading(true);
    try {
      await UsersServiceHelpers.forceChangePassword(newPassword);
      setSuccess(true);
      setTimeout(() => {
        dispatch(logout());
        navigate('/login');
      }, 2500);
    } catch (err) {
      const msg = extractErrorMessage(err, 'No se pudo cambiar la contraseña. Intente de nuevo.');
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#f3f4f6',
      borderRadius: '0.5rem',
      '& fieldset': { borderColor: 'rgba(12, 187, 154, 0.45)' },
      '&:hover fieldset': { borderColor: colors.success.main },
      '&.Mui-focused fieldset': {
        borderColor: '#0BB296',
        boxShadow: '0 0 0 2px rgba(12, 187, 154, 0.2)',
      },
    },
    '& .MuiInputBase-input': { py: 1.5 },
  };

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        overflow: 'hidden',
      }}
    >
      <BackgroundSVG />

      <Container component="main" maxWidth="sm" sx={{ position: 'relative', zIndex: 10 }}>
        <Box
          sx={{
            position: 'relative',
            borderRadius: '0.75rem',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
            width: '100%',
            maxWidth: '32rem',
            mx: 'auto',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              borderRadius: '0.75rem',
              background: 'linear-gradient(to right, #22d3ee, #10b981, #22d3ee)',
              opacity: 0.7,
              filter: 'blur(4px)',
            }}
          />

          <Paper
            elevation={0}
            sx={{
              position: 'relative',
              borderRadius: '0.75rem',
              bgcolor: 'rgba(255, 255, 255, 0.95)',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                px: 4,
                pt: 4,
                pb: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <LlamaLogo />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, mt: 1 }}>
                <LockReset sx={{ color: '#10b981', fontSize: 28 }} />
                <Typography
                  component="h1"
                  variant="h5"
                  sx={{ fontWeight: 'bold', color: '#1f2937' }}
                >
                  Cambio de Contraseña
                </Typography>
              </Box>
              <Typography variant="body2" align="center" sx={{ color: '#6b7280', mb: 1 }}>
                Establezca una nueva contraseña para continuar.
              </Typography>
            </Box>

            {error && (
              <Box sx={{ px: 4 }}>
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              </Box>
            )}

            {success && (
              <Box sx={{ px: 4, mb: 2 }}>
                <Typography variant="body2" align="center" sx={{ color: '#6b7280' }}>
                  Contraseña actualizada con éxito. Redirigiendo al inicio de sesión...
                </Typography>
              </Box>
            )}

            <Box component="form" onSubmit={handleSubmit} noValidate sx={{ px: 4, pb: 4 }}>
              <Typography
                variant="subtitle2"
                sx={{ mb: 0.5, fontWeight: 'bold', color: '#4b5563' }}
              >
                Nueva Contraseña
              </Typography>
              <TextField
                fullWidth
                required
                type={showPassword ? 'text' : 'password'}
                placeholder="Mín. 8 caracteres, 1 mayúscula, 1 número"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={isLoading || success}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock fontSize="small" sx={{ color: '#6b7280' }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        sx={{ color: '#6b7280' }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ ...inputSx, mb: 2 }}
              />

              <Typography
                variant="subtitle2"
                sx={{ mb: 0.5, fontWeight: 'bold', color: '#4b5563' }}
              >
                Confirmar Contraseña
              </Typography>
              <TextField
                fullWidth
                required
                type={showConfirm ? 'text' : 'password'}
                placeholder="Repita la nueva contraseña"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={isLoading || success}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock fontSize="small" sx={{ color: '#6b7280' }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirm(!showConfirm)}
                        edge="end"
                        sx={{ color: '#6b7280' }}
                      >
                        {showConfirm ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ ...inputSx, mb: 3 }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={isLoading || success}
                sx={{
                  py: 1.5,
                  fontSize: '1rem',
                  mb: 2,
                  textTransform: 'uppercase',
                  fontWeight: 500,
                  borderRadius: '0.5rem',
                  background: 'linear-gradient(to right, #06b6d4, #10b981)',
                  '&:hover': {
                    background: 'linear-gradient(to right, #0891b2, #059669)',
                    transform: 'translateY(-1px)',
                  },
                  transition: 'all 0.3s',
                }}
              >
                {isLoading ? 'Guardando...' : 'ESTABLECER NUEVA CONTRASEÑA'}
              </Button>

              <Box sx={{ py: 2, mt: 1, textAlign: 'center', borderTop: 1, borderColor: '#e5e7eb' }}>
                <Typography variant="caption" sx={{ color: '#6b7280' }}>
                  Su sesión expirará en 10 minutos si no cambia su contraseña.
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Box>
      </Container>
    </Box>
  );
};

export default ForceChangePasswordPage;
